import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Bell,
  CheckCheck,
  ChevronDown,
  Loader2,
  MessageCircle,
  Send,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatError } from "@/lib/api";

const statusLabels = {
  open: "Open",
  in_progress: "In progress",
  waiting_customer: "Waiting for you",
  resolved: "Resolved",
};

const formatDate = (value) => {
  if (!value) {
    return "";
  }
  return new Intl.DateTimeFormat("en-IN", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(value));
};

const timelineForTicket = (ticket) => {
  const entries = [
    {
      id: `initial-${ticket.id}`,
      author: "customer",
      message: ticket.message,
      created_at: ticket.created_at,
    },
  ];
  (ticket.conversation || []).forEach((entry, index) => {
    const isCustomer = entry.author === "customer" || entry.role === "customer";
    const message = entry.message || entry.content || "";
    if (!isCustomer || !message) {
      return;
    }
    if (message === ticket.message && index === 0) {
      return;
    }
    entries.push({
      id: entry.id || `customer-${index}`,
      author: "customer",
      message,
      created_at: entry.created_at,
    });
  });
  (ticket.admin_replies || []).forEach((reply, index) => {
    entries.push({
      id: reply.id || `admin-${index}`,
      author: "admin",
      message: reply.message,
      created_at: reply.created_at,
      admin_name: reply.admin_name,
    });
  });
  return entries.sort((left, right) => (
    String(left.created_at || "").localeCompare(String(right.created_at || ""))
  ));
};

export const CustomerSupportTickets = ({ focusTicketId = "" }) => {
  const [tickets, setTickets] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [drafts, setDrafts] = useState({});
  const [openTicketId, setOpenTicketId] = useState(focusTicketId);
  const [loading, setLoading] = useState(true);
  const [sendingTicketId, setSendingTicketId] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [ticketResponse, notificationResponse] = await Promise.all([
        api.get("/me/support-tickets"),
        api.get("/me/notifications"),
      ]);
      setTickets(ticketResponse.data);
      setNotifications(notificationResponse.data.filter((item) => item.ticket_id));
    } catch (error) {
      toast.error(formatError(error));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (focusTicketId) {
      setOpenTicketId(focusTicketId);
    }
  }, [focusTicketId]);

  const unreadNotifications = useMemo(
    () => notifications.filter((notification) => !notification.read),
    [notifications],
  );

  const markTicketUpdatesRead = async (ticketId) => {
    const unreadForTicket = notifications.filter((notification) => (
      notification.ticket_id === ticketId && !notification.read
    ));
    if (unreadForTicket.length === 0) {
      return;
    }
    try {
      await Promise.all(unreadForTicket.map((notification) => (
        api.put(`/me/notifications/${notification.id}/read`)
      )));
      setNotifications((current) => current.map((notification) => (
        notification.ticket_id === ticketId ? { ...notification, read: true } : notification
      )));
    } catch {
      // Reading an update must not block the ticket conversation.
    }
  };

  const openTicket = async (ticketId) => {
    setOpenTicketId((current) => (current === ticketId ? "" : ticketId));
    await markTicketUpdatesRead(ticketId);
  };

  const sendReply = async (ticketId) => {
    const message = (drafts[ticketId] || "").trim();
    if (!message) {
      return;
    }
    setSendingTicketId(ticketId);
    try {
      await api.post(`/me/support-tickets/${ticketId}/reply`, { message });
      setDrafts((current) => ({ ...current, [ticketId]: "" }));
      toast.success("Your reply has been sent to Eira Support");
      await load();
      setOpenTicketId(ticketId);
    } catch (error) {
      toast.error(formatError(error));
    } finally {
      setSendingTicketId("");
    }
  };

  return (
    <section className="space-y-5" data-testid="customer-support-tickets">
      <header className="border-b border-[#E2DCD2] pb-5">
        <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[#8C6D46]">
          Customer care
        </p>
        <div className="mt-2 flex flex-wrap items-end justify-between gap-4">
          <div>
            <h2 className="font-serif text-3xl text-[#1E2A24]">Support tickets</h2>
            <p className="mt-1 text-sm text-[#64748B]">
              Follow every conversation with Eira Support in one place.
            </p>
          </div>
          <div
            className={[
              "inline-flex items-center gap-2 border border-[#D9C3A5] bg-[#FDFBF7]",
              "px-3 py-2 text-sm",
            ].join(" ")}
            data-testid="support-ticket-unread-count"
          >
            <Bell size={15} className="text-[#C86D42]" />
            {unreadNotifications.length} unread update
            {unreadNotifications.length === 1 ? "" : "s"}
          </div>
        </div>
      </header>

      {unreadNotifications.length > 0 && (
        <div
          className="border border-[#D9C3A5] bg-[#FEF8EF] p-4"
          data-testid="support-ticket-notifications"
        >
          <div className="flex items-center gap-2 text-sm font-semibold text-[#705532]">
            <Bell size={15} /> New support updates
          </div>
          <div className="mt-3 space-y-2">
            {unreadNotifications.slice(0, 3).map((notification) => (
              <button
                key={notification.id}
                type="button"
                onClick={() => openTicket(notification.ticket_id)}
                className={[
                  "block w-full border border-[#E9D5B8] bg-white px-3 py-2 text-left",
                  "text-sm hover:bg-[#FDFBF7]",
                ].join(" ")}
                data-testid={`open-ticket-notification-${notification.id}`}
              >
                <span className="block font-semibold text-[#1E2A24]">
                  {notification.title}
                </span>
                <span className="mt-1 block text-[#64748B]">{notification.body}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex min-h-48 items-center justify-center text-sm text-[#64748B]">
          <Loader2 size={17} className="mr-2 animate-spin" /> Loading support tickets…
        </div>
      ) : tickets.length === 0 ? (
        <div
          className="border border-[#E2DCD2] bg-white px-6 py-12 text-center"
          data-testid="customer-support-tickets-empty"
        >
          <MessageCircle size={22} className="mx-auto text-[#C86D42]" />
          <p className="mt-3 text-sm text-[#64748B]">
            You do not have any support tickets yet.
          </p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="customer-support-ticket-list">
          {tickets.map((ticket) => {
            const isOpen = openTicketId === ticket.id;
            const timeline = timelineForTicket(ticket);
            return (
              <article
                key={ticket.id}
                className="border border-[#E2DCD2] bg-white"
                data-testid={`customer-support-ticket-${ticket.id}`}
              >
                <button
                  type="button"
                  onClick={() => openTicket(ticket.id)}
                  className={[
                    "flex w-full items-start justify-between gap-4 p-5 text-left",
                    "hover:bg-[#FDFBF7]",
                  ].join(" ")}
                  data-testid={`open-customer-support-ticket-${ticket.id}`}
                >
                  <span className="min-w-0">
                    <span
                      className={[
                        "flex items-center gap-2 text-xs font-semibold uppercase",
                        "tracking-[0.12em] text-[#8C6D46]",
                      ].join(" ")}
                    >
                      <MessageCircle size={14} />
                      Ticket #{ticket.id.slice(0, 8).toUpperCase()}
                    </span>
                    <span className="mt-2 block line-clamp-2 text-sm text-[#334155]">
                      {ticket.message}
                    </span>
                    <span className="mt-2 block text-xs text-[#64748B]">
                      {formatDate(ticket.updated_at)}
                    </span>
                  </span>
                  <span className="flex shrink-0 items-center gap-2">
                    <span
                      className={`px-2 py-1 text-xs font-semibold ${
                        ticket.status === "resolved"
                          ? "bg-emerald-50 text-emerald-800"
                          : "bg-[#EBF2EE] text-[#2D5A47]"
                      }`}
                      data-testid={`customer-support-ticket-status-${ticket.id}`}
                    >
                      {statusLabels[ticket.status] || ticket.status}
                    </span>
                    <ChevronDown
                      size={17}
                      className={[
                        "text-[#64748B] transition-transform",
                        isOpen ? "rotate-180" : "",
                      ].join(" ")}
                    />
                  </span>
                </button>

                {isOpen && (
                  <div className="border-t border-[#E2DCD2] p-5">
                    <div
                      className="space-y-3"
                      data-testid={`customer-ticket-timeline-${ticket.id}`}
                    >
                      {timeline.map((entry) => (
                        <div
                          key={entry.id}
                          className={`max-w-[88%] px-3 py-2 text-sm ${
                            entry.author === "admin"
                              ? "border-l-2 border-[#2D5A47] bg-[#F2F7F3]"
                              : "ml-auto border-r-2 border-[#C86D42] bg-[#FEF6EF]"
                          }`}
                          data-testid={`customer-ticket-message-${ticket.id}-${entry.id}`}
                        >
                          <p className="text-xs font-semibold text-[#1E2A24]">
                            {entry.author === "admin"
                              ? entry.admin_name || "Eira Support"
                              : "You"}
                          </p>
                          <p className="mt-1 whitespace-pre-wrap text-[#334155]">
                            {entry.message}
                          </p>
                          <p className="mt-1 text-[11px] text-[#64748B]">
                            {formatDate(entry.created_at)}
                          </p>
                        </div>
                      ))}
                    </div>
                    {ticket.status !== "resolved" && (
                      <div className="mt-5 border-t border-[#E2DCD2] pt-4">
                        <label
                          className={[
                            "block text-xs font-semibold uppercase tracking-[0.12em]",
                            "text-[#8C6D46]",
                          ].join(" ")}
                        >
                          Reply to support
                          <textarea
                            rows={3}
                            value={drafts[ticket.id] || ""}
                            onChange={(event) => setDrafts((current) => ({
                              ...current,
                              [ticket.id]: event.target.value,
                            }))}
                            placeholder="Add more details for the support team…"
                            className={[
                              "mt-2 w-full border border-[#E2DCD2] px-3 py-2.5 text-sm",
                              "outline-none focus:border-[#2D5A47]",
                            ].join(" ")}
                            data-testid={`customer-ticket-reply-input-${ticket.id}`}
                          />
                        </label>
                        <button
                          type="button"
                          onClick={() => sendReply(ticket.id)}
                          disabled={
                            !String(drafts[ticket.id] || "").trim()
                            || sendingTicketId === ticket.id
                          }
                          className={[
                            "mt-3 inline-flex items-center gap-2 bg-[#2D5A47] px-4 py-2",
                            "text-sm font-semibold text-white hover:bg-[#214435]",
                            "disabled:opacity-50",
                          ].join(" ")}
                          data-testid={`customer-ticket-reply-send-${ticket.id}`}
                        >
                          {sendingTicketId === ticket.id ? (
                            <Loader2 size={15} className="animate-spin" />
                          ) : (
                            <Send size={15} />
                          )}
                          Send reply
                        </button>
                      </div>
                    )}
                    {ticket.status === "resolved" && (
                      <p className="mt-5 flex items-center gap-2 text-sm text-[#2D5A47]">
                        <CheckCheck size={16} /> This ticket is resolved.
                      </p>
                    )}
                  </div>
                )}
              </article>
            );
          })}
        </div>
      )}
    </section>
  );
};